/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./searchbar-component.module.scss";
import searchIcon from "@src/assets/svg/search.svg";
import clearIcon from "@src/assets/svg/close-icon.svg";
import { VerticalSeparator } from "@src/components/atoms/vertical-separator/vertical-separator";
import { useI18N } from "@src/language/language-context";

const cssClassName = "searchbar";

type SearchBarTexts = {
  title: string;
  search: string;
  remove: string;
  clear: string;
};

type SearchBarMeta = {
  searchTerms: string[];
  searchQuery: string;
  isActive: boolean;
};

interface SearchBarProps<TData, TResult> {
  data: TData | null | undefined;
  searchFn: (data: TData, searchTerms: string[]) => TResult;
  emptyResult: TResult;
  onSearchResults?: (result: TResult, meta: SearchBarMeta) => void;
  initialSearchTerms?: string[];
  initialSearchQuery?: string;
  searchTerms?: string[];
  searchQuery?: string;
  onSearchTermsChange?: (searchTerms: string[]) => void;
  onSearchQueryChange?: (searchQuery: string) => void;
  texts?: Partial<SearchBarTexts>;
  showShortcut?: boolean;
}

export const SearchBar = <TData, TResult>({
  data,
  searchFn,
  emptyResult,
  onSearchResults,
  initialSearchTerms = [],
  initialSearchQuery = "",
  // When provided, search terms are controlled by the parent (e.g. Redux).
  searchTerms: controlledSearchTerms,
  searchQuery: controlledQuery,
  onSearchTermsChange,
  onSearchQueryChange,
  texts,
  showShortcut = true,
}: SearchBarProps<TData, TResult>) => {
  const i18n = useI18N();
  const [internalSearchQuery, setInternalSearchQuery] =
    useState<string>(initialSearchQuery);
  const [internalSearchedFields, setInternalSearchedFields] =
    useState<string[]>(initialSearchTerms);
  // const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState<boolean>(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const focusInput = () =>
    requestAnimationFrame(() => inputRef.current?.focus());

  const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isMac = /Mac|iPhone|iPad/.test(userAgent);
  const shortcutText = isMac ? "Cmd + F" : "Ctrl + F";

  const searchTexts: SearchBarTexts = {
    title: i18n("modules.search.title"),
    search: i18n("modules.search.search"),
    remove: i18n("modules.search.remove"),
    clear: i18n("modules.search.clear"),
    ...texts,
  };

  const searchTerms = controlledSearchTerms ?? internalSearchedFields;
  const searchQuery = controlledQuery ?? internalSearchQuery;
  const isSearchTermsControlled = typeof controlledSearchTerms !== "undefined";
  const isQueryControlled = typeof controlledQuery !== "undefined";

  const setSearchTerms = (nextSearchTerms: string[]) => {
    if (isSearchTermsControlled) {
      onSearchTermsChange?.(nextSearchTerms);
      return;
    }
    setInternalSearchedFields(nextSearchTerms);
  };

  const setSearchQuery = (nextQuery: string) => {
    if (isQueryControlled) {
      onSearchQueryChange?.(nextQuery);
      return;
    }
    setInternalSearchQuery(nextQuery);
  };

  const searchResults = useMemo(() => {
    if (!data || searchTerms.length === 0) return emptyResult;
    return searchFn(data, searchTerms);
  }, [data, searchTerms, emptyResult, searchFn]);

  useEffect(() => {
    if (!onSearchResults) return;
    onSearchResults(searchResults, {
      searchTerms,
      searchQuery,
      isActive: searchTerms.length > 0,
    });
  }, [onSearchResults, searchResults, searchTerms, searchQuery]);

  useEffect(() => {
    if (!showShortcut) return undefined;
    const handleShortcut = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "f") {
        event.preventDefault();
        setIsSearchExpanded(true);
        focusInput();
      }
    };

    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, [showShortcut]);

  const handleAddTerm = (value: string) => {
    const term = value.trim();
    if (!term) return;
    const normalizedTerm = term.toLowerCase();
    const hasDuplicate = searchTerms.some(
      (existing) => existing.toLowerCase() === normalizedTerm,
    );
    if (hasDuplicate) {
      setSearchQuery("");
      return;
    }
    setSearchTerms([...searchTerms, term]);
    setSearchQuery("");
  };

  const handleRemoveTerm = (term: string) => {
    setSearchTerms(searchTerms.filter((t) => t !== term));
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setSearchTerms([]);
    focusInput();
  };

  const handleBlur = () => {
    if (!searchQuery.trim() && searchTerms.length === 0) {
      setIsSearchExpanded(false);
    }
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleAddTerm(searchQuery);
    }

    if (
      event.key === "Backspace" &&
      searchQuery.trim().length === 0 &&
      searchTerms.length > 0
    ) {
      handleRemoveTerm(searchTerms[searchTerms.length - 1]);
    }
  };

  const handleSearchClick = () => {
    handleAddTerm(searchQuery);
    focusInput();
  };

  const showSearchInput =
    isSearchExpanded || searchQuery.trim().length > 0 || searchTerms.length > 0;

  const handleExpandAndFocus = () => {
    setIsSearchExpanded(true);
    focusInput();
  };

  return (
    <div className={styles[cssClassName]}>
      {/* Todo: Implement filter dropdown */}
      {/* <button
        className={styles[`${cssClassName}__filter-btn`]}
        onClick={() => setIsFilterOpen((prev) => !prev)}
      >
        <img
          src={filterIcon}
          alt={texts.filter}
          className={styles[`${cssClassName}__filter-icon`]}
        />
        <img
          src={caretDown}
          alt={texts.caretDown}
          className={cn(
            styles[`${cssClassName}__filter-icon`],
            styles[`${cssClassName}__caret-icon`],
            { [styles[`${cssClassName}__caret-icon--open`]]: isFilterOpen },
          )}
        />
      </button>

      <VerticalSeparator /> */}

      <div
        className={styles[`${cssClassName}__search-wrapper`]}
        onClick={handleExpandAndFocus}
      >
        {!showSearchInput ? (
          <div
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleExpandAndFocus();
              }
            }}
            className={styles[`${cssClassName}__placeholder-text`]}
          >
            <img
              src={searchIcon}
              alt={searchTexts.search}
              className={styles[`${cssClassName}__search-icon`]}
            />
            <span>{searchTexts.title}</span>
            {showShortcut && (
              <strong className={styles[`${cssClassName}__shortcut-badge`]}>
                {shortcutText}
              </strong>
            )}
          </div>
        ) : (
          <div className={styles[`${cssClassName}__search-input-wrapper`]}>
            <button
              type="button"
              className={styles[`${cssClassName}__search-trigger`]}
              onClick={handleSearchClick}
            >
              <img
                src={searchIcon}
                alt={searchTexts.search}
                className={styles[`${cssClassName}__search-icon`]}
              />
            </button>

            <div className={styles[`${cssClassName}__tokens`]}>
              {searchTerms.map((term) => (
                <span key={term} className={styles[`${cssClassName}__token`]}>
                  {term}
                  <VerticalSeparator />
                  <button
                    type="button"
                    className={styles[`${cssClassName}__token-remove`]}
                    onClick={() => handleRemoveTerm(term)}
                    aria-label={`Remove ${term}`}
                  >
                    <img
                      src={clearIcon}
                      alt={searchTexts.remove}
                      className={styles[`${cssClassName}__token-remove-icon`]}
                    />
                  </button>
                </span>
              ))}

              <input
                ref={inputRef}
                type="text"
                className={styles[`${cssClassName}__search-input`]}
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                onKeyDown={handleInputKeyDown}
                onBlur={handleBlur}
                aria-label={searchTexts.search}
              />
            </div>

            {(searchQuery.trim().length > 0 || searchTerms.length > 0) && (
              <button
                type="button"
                className={styles[`${cssClassName}__clear-btn`]}
                onClick={handleClearSearch}
              >
                <img
                  src={clearIcon}
                  alt={searchTexts.clear}
                  className={styles[`${cssClassName}__clear-icon`]}
                />
              </button>
            )}

            {showShortcut && (
              <strong className={styles[`${cssClassName}__shortcut-badge`]}>
                {shortcutText}
              </strong>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
